import {
  useCompanyNavbarMobileIsOpen,
  useCompanyNavbarMobileIsOpenValue,
  useSetCompanyNavbarMobileIsOpen,
} from "components/company/v3/menu/hooks";
import classNames from "components/ui/classNames";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEventListener } from "usehooks-ts";

interface CompanyNavbarPortalProps {
  children: React.ReactNode;
  className?: string;
}

export function CompanyNavbarPortal({ children, className }: CompanyNavbarPortalProps) {
  const [isOpen, setIsOpen] = useCompanyNavbarMobileIsOpen();

  useEventListener("resize", (event: any) => {
    if (isOpen && event.target.innerWidth > 1024) {
      setIsOpen(!isOpen);
    }
  });

  return (
    <div
      className={classNames(
        "sticky left-0 top-navbar-mobile-offset z-10 w-full lg:top-navbar-desktop-offset",
        className,
        {
          "fixed bottom-0 w-full": isOpen,
          "h-16": !isOpen,
        },
      )}
    >
      {children}
    </div>
  );
}

interface CompanyNavbarContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CompanyNavbarContent({ children, className }: CompanyNavbarContentProps) {
  return (
    <div
      className={classNames(
        "relative flex w-full items-center gap-6 bg-white lg:px-4 lg:py-2",
        "border-b border-slate-200 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
        "dark:border-neutral-700 dark:bg-slate-900 dark:ring-transparent dark:ring-offset-transparent",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CompanyNavbarMenuOptionProps {
  url: string;
  isActive: boolean;
  children: React.ReactNode;
  className?: string;
}

export function CompanyNavbarMenuOption({ url, isActive, children, className }: CompanyNavbarMenuOptionProps) {
  return (
    <Link
      href={url}
      className={classNames(
        "relative flex flex-col items-center space-y-0.5",
        {
          "text-blue-500 dark:font-medium dark:text-slate-100": isActive,
          "text-gray-500 dark:text-slate-400": !isActive,
        },
        className,
      )}
      shallow={true}
    >
      {children}
    </Link>
  );
}

interface MobileCompanyNavbarTriggerProps {
  children: React.ReactNode;
}

export function MobileCompanyNavbarTrigger({ children }: MobileCompanyNavbarTriggerProps) {
  const setSidebarMobileIsOpen = useSetCompanyNavbarMobileIsOpen();

  return (
    <button
      type="button"
      className="sapce-x-1 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-bold text-blue-600 focus-visible:outline-none dark:bg-blue-900/30 dark:text-blue-300"
      onClick={() => setSidebarMobileIsOpen((isOpen) => !isOpen)}
    >
      {children}
    </button>
  );
}

interface MobileCompanyNavbarContentProps {
  children: React.ReactNode;
}

export function MobileCompanyNavbarContent({ children }: MobileCompanyNavbarContentProps) {
  const isOpen = useCompanyNavbarMobileIsOpenValue();

  return (
    <aside
      className={classNames("z-10 h-[calc(100dvh-124px)] w-full overflow-y-scroll px-4 pb-2", {
        hidden: !isOpen,
      })}
    >
      <div className="flex h-max w-full flex-col">{children}</div>
    </aside>
  );
}

interface MobileCompanyNavbarMenuOptionProps {
  href: string;
  title: string;
  icon: React.ReactNode;
  titleClassName?: string;
  iconClassName?: string;
  className?: string;
}

export function MobileCompanyNavbarMenuOption({
  href,
  title,
  icon,
  titleClassName,
  iconClassName,
  className,
  ...props
}: MobileCompanyNavbarMenuOptionProps) {
  const pathname = usePathname();
  const isActive = pathname.includes(href);

  return (
    <Link
      href={href}
      passHref={true}
      className={classNames(
        "group",
        "py-2",
        "tracking-wide text-gray-600",
        "hover:bg-slate-100 focus:outline-none",
        "flex items-center space-x-4",
        "rounded-full px-2 dark:text-slate-300 dark:hover:bg-slate-800",
        className,
      )}
      {...props}
    >
      <div className={classNames("z-0 flex h-10 w-10 items-center justify-center")}>
        <div className={classNames("inline-flex rounded-2xl p-2.5 squircle", iconClassName)}>{icon}</div>
      </div>

      <span
        className={classNames("text-base font-medium text-slate-600 dark:text-slate-300", titleClassName, {
          "text-blue-600 dark:text-blue-400": isActive,
        })}
      >
        {title}
      </span>
    </Link>
  );
}
